<template>
  <div>
    <cl-table-wrapper class-name="tw-bg-primary-grey-25 tw-px-0">
      <template #before>
        <div class="tw-grid tw-grid-cols-12 tw-gap-1 tw-mb-4">
          <div class="tw-col-span-6 max-md:tw-col-span-12">
            <label
              for="msp-licence-usage-customers-table-search"
              class="tw-sr-only"
              >Search
            </label>
            <div class="tw-relative md:tw-max-w-xs">
              <cl-input-search
                id="msp-licence-usage-customers-table-search"
                :name="'msp-licence-usage-customers-table-search'"
                test-id="msp-licence-usage-customers-table-search"
                :placeholder="$t('search')"
                :value="filterState"
                :readonly="loading"
                @update:value="debounceSearch"
              />
            </div>
          </div>
          <div
            class="tw-col-span-6 max-md:tw-col-span-12 tw-flex tw-items-center tw-justify-end tw-gap-2"
          >
            <customer-export-button-action
              :accounts-to-export="customersList"
              :headers="table.getFlatHeaders()"
              :selected-service-uuid="selectedServiceUuid"
            />
            <cl-table-columns-configuration-popover
              :configure-columns-text="$t('configure-columns')"
              :reset-order-text="$t('controls-default')"
              :reset-btn="{
                disabled:
                  JSON.stringify(columnOrder) ===
                  JSON.stringify(DEFAULT_COLUMN_ORDER),
              }"
              @reset="resetColumnSizing"
            >
              <cl-table-columns-configuration-popover-item
                v-for="(column, index) in columnsToggleList"
                :key="column.id"
                :class="{
                  'tw-border-dashed tw-border-primary-grey-500':
                    targetItemIndex === index,
                  'tw-border-white': targetItemIndex !== index,
                }"
                draggable="true"
                @dragstart="onDragStart($event, index)"
                @dragover.prevent
                @drop="onDrop(index)"
                @dragenter="onDragEnter(index)"
                @dragend="onDragEnd"
              >
                <label
                  class="tw-flex tw-items-center tw-gap-2 tw-ml-1 hover:tw-cursor-pointer"
                  :for="column.id"
                >
                  <cl-input-checkbox
                    :id="column.id"
                    :checked="column.getIsVisible()"
                    :disabled="
                      column.getIsVisible() &&
                      table.getVisibleLeafColumns().length <= 2
                    "
                    :indeterminate="false"
                    :name="column.id"
                    @change="toggleColumnVisibility(column)"
                  />
                  {{ column.columnDef.header }}
                </label>
              </cl-table-columns-configuration-popover-item>
            </cl-table-columns-configuration-popover>
          </div>
        </div>
      </template>
      <template #table>
        <cl-table>
          <cl-thead>
            <cl-tr
              v-for="headerGroup in table.getHeaderGroups()"
              :key="headerGroup.id"
              :table-target="'thead'"
              class="tw-flex tw-w-full"
            >
              <cl-th
                v-for="header in headerGroup.headers"
                :key="header.id"
                :col-span="header.colSpan"
                class="tw-block"
                :class="{
                  'tw-select-none': header.column.getCanSort(),
                  'tw-sticky tw-right-0 tw-bg-[inherit] tw-ml-auto':
                    header.column.getIsPinned() === 'right',
                }"
                scope="col"
                :style="{ width: `${header.getSize()}px` }"
              >
                <cl-table-header-cell
                  :header="header"
                  :loading="loading"
                  test-id="msp-licence-usage-customers-table-header"
                >
                  <FlexRender
                    :render="header.column.columnDef.header"
                    :props="header.getContext()"
                  />
                </cl-table-header-cell>
              </cl-th>
            </cl-tr>
          </cl-thead>
          <cl-tbody>
            <!-- Pending -->
            <template v-if="loading">
              <cl-tr
                v-for="(_row, index) in skeletonArray"
                :key="index"
                class="tw-border-b tw-border-primary-grey-100/80 hover:tw-bg-primary-grey-50 tw-bg-white"
              >
                <cl-td
                  class="h-2 bg-slate-200 rounded tw-animate-pulse tw-whitespace-nowrap tw-px-2"
                >
                  <div
                    class="tw-w-30 tw-h-5 tw-animate-pulse tw-bg-primary-grey-25"
                  />
                </cl-td>
              </cl-tr>
            </template>
            <!-- No Data -->
            <template v-else-if="!customersList.length">
              <cl-tr zebra>
                <cl-td
                  colspan="100%"
                  :class-name="'tw-text-center tw-pl-0 tw-pr-0 tw-pb-0'"
                >
                  <t-empty>
                    <template #image>
                      <component :is="emptyState.icon" />
                    </template>
                    <template #title>
                      {{ emptyState.title }}
                    </template>
                    <template #description>
                      {{ emptyState.description }}
                    </template>
                  </t-empty>
                </cl-td>
              </cl-tr>
            </template>
            <!-- Render Data -->
            <template v-else>
              <cl-tr
                v-for="(row, index) in table.getRowModel().rows"
                :key="row.id"
                :zebra="index % 2 === 0"
                class="tw-flex tw-w-full"
                data-clarity-mask="true"
              >
                <cl-td
                  v-for="cell in row.getVisibleCells()"
                  :key="cell.id"
                  class="tw-overflow-hidden tw-whitespace-nowrap tw-text-ellipsis tw-bg-[inherit]"
                  :class="{
                    'tw-sticky tw-right-0 tw-bg-[inherit] tw-ml-auto':
                      cell.column.getIsPinned() === 'right',
                  }"
                  :style="{ width: `${cell.column.getSize()}px` }"
                >
                  <FlexRender
                    :render="cell.column.columnDef.cell"
                    :props="cell.getContext()"
                  />
                </cl-td>
              </cl-tr>
            </template>
          </cl-tbody>
        </cl-table>
      </template>
      <template #after>
        <div class="tw-relative">
          <div class="tw-flex tw-justify-between tw-items-baseline">
            <cl-table-page-size
              :placeholder="$t('rows-per-page')"
              :page-size="table.getState().pagination.pageSize"
              :page-sizes="[10, 25, 50]"
              :disabled="loading"
              :handle-page-size-change="
                ($e: Event) =>
                  handlePageSizeChange(
                    Number(($e.target as HTMLInputElement).value),
                  )
              "
            />
            <cl-table-pagination-state
              :start="rowsIndexPointer.startIndex"
              :end="rowsIndexPointer.endIndex"
              :total="total.total"
              :placeholder="$t('of')"
            />
            <cl-table-basic-pagination
              :previous-page-text="$t('previous-pagination')"
              :next-page-text="$t('next')"
              :can-previous-page="table.getCanPreviousPage()"
              :previous-page="table.previousPage"
              :can-next-page="table.getCanNextPage()"
              :next-page="table.nextPage"
              :disabled="loading"
            />
          </div>
        </div>
      </template>
    </cl-table-wrapper>
  </div>
</template>

<script setup lang="ts">
import { useAuthenticationStore } from "@/store/authentication.store";
import { storeToRefs } from "pinia";
import { useOrganisationsStore } from "@/store/organisations.store";
import { useLicenceStore } from "@/modules/Usage/store/licence.store";
import { useAzureStore } from "@/store/azure.store";

import TEmpty from "@/components/TEmpty";
import { computed, h, ref, toRefs } from "vue";
import { useI18n } from "vue-i18n";
import { watch } from "vue";
import { useRouter } from "vue-router";
import { useGlobalOrganisationIDBasedOnRoute } from "@/composables/useGlobalOrganisationIDBasedOnRoute";
import {
  useRowsIndexPointer,
  useSkeletonArray,
  useServerSidePagination,
  useMemoDefaultParams,
  useMemoColumnVisibility,
  useMemoDefaultColumnSizing,
  useMemoDefaultColumnOrder,
  useColumnVisibility,
  useColumnOrder,
  calcColumnDynamicSize,
} from "@/composables/table-helpers";
import { useDebounceFn } from "@vueuse/core";
import {
  useVueTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  FlexRender,
  ColumnDef,
} from "@tanstack/vue-table";
import { transformOrganisationsToCustomers } from "./utils/transformOrganisationsToCustomers";
import { redirectToCustomerUsage } from "../navigation";
import dayjs from "dayjs";
import CustomerViewButton from "./CustomerViewButton/CustomerViewButton.vue";
import CustomerExportButtonAction from "./CustomerExportButtonAction/CustomerExportButtonAction.vue";
import { Customer } from "@/modules/Usage/views/Msp/Overview/CustomersList/types";
import { DATA_TABLE_COOKIE } from "@/constants";
import {
  MIN_COLUMN_SIZE,
  MAX_COLUMN_SIZE,
  RATIO_SIZE,
} from "@/modules/PhishingSimulations/constants";

type Props = {
  selectedServiceUuid: string;
};
const props = defineProps<Props>();
const { selectedServiceUuid } = toRefs(props);

const { globalOrganisationId } = useGlobalOrganisationIDBasedOnRoute();

const loading = ref(true);
const cookieKey = ref<string>(DATA_TABLE_COOKIE.CUSTOMER_USAGE_CUSTOMERS_LIST);
const authStore = useAuthenticationStore();
const organisationsStore = useOrganisationsStore();
const {
  orginisationsList: organisationsList,
  organisationsTotal: total,
  organisationsConnections,
} = storeToRefs(organisationsStore);
const licenceStore = useLicenceStore();
const azureStore = useAzureStore();

const customersList = computed(() =>
  transformOrganisationsToCustomers(
    organisationsList.value as unknown as Parameters<
      typeof transformOrganisationsToCustomers
    >[0],
    organisationsConnections.value,
  ),
);

const getOrganisationsList = async (solution_uuid: string) => {
  loading.value = true;

  try {
    await organisationsStore.getOrginisationsList({
      parent_uuid: globalOrganisationId.value,
      params: {
        solution_uuid: solution_uuid,
        page: paramsState.page,
        results: paramsState.results,
        sortDir: paramsState.sortDir.toLocaleUpperCase() || "ASC",
        sortKey: paramsState.sortKey || "name",
        filter: paramsState.filter || "",
      },
      with_connections: true,
    });
  } finally {
    loading.value = false;
  }
};

const setCustomer = async (payload: { id: string; name: string }) => {
  try {
    await authStore.setCustomer({
      uuid: payload.id,
      name: payload.name,
    });
  } catch {
    // Handled in axios interceptor
  }
};

const clearAzureCache = async () => {
  try {
    await azureStore.clearAzureCache();
  } catch {
    // Handled in axios interceptor
  }
};

const clearLicenceCache = async () => {
  try {
    await licenceStore.clearLicenceCache();
  } catch {
    // Handled in axios interceptor
  }
};

const { t } = useI18n();

const columns: ColumnDef<Customer>[] = [
  {
    header: t("customer"),
    accessorKey: "name",
  },
  {
    header: t("licenses-issued"),
    accessorKey: "seats_purchased",
  },
  {
    header: t("active-users"),
    accessorKey: "seats_used",
  },
  {
    header: t("total-usage"),
    accessorKey: "total_usage",
    enableSorting: true,
    cell: ({ getValue }) => {
      const percentage = Math.round(getValue<number>() * 100) || 0;

      const parentClass = "tw-inline-flex";

      if (percentage >= 100) {
        return h(
          "span",
          {
            class: parentClass,
          },
          [
            h("span", {
              class: "tw-text-system-error-500",
              innerHTML: `${percentage}%`,
            }),
          ],
        );
      }

      if (percentage >= 80 && percentage < 100) {
        return h(
          "span",
          {
            class: parentClass,
          },
          [
            h("span", {
              class: "tw-text-system-warning-500",
              innerHTML: `${percentage}%`,
            }),
          ],
        );
      }

      return h(
        "span",
        {
          class: parentClass,
        },
        [
          h("span", {
            innerHTML: `${percentage}%`,
          }),
        ],
      );
    },
  },
  {
    header: t("date-created"),
    accessorKey: "created_at",
    cell: ({ getValue }) => dayjs(getValue<Date>()).format("MMM D, YYYY"),
  },
  {
    header: t("actions"),
    accessorKey: "actions",
    enableSorting: false,
    enableHiding: false,
    enableResizing: false,
    size: 80,
    cell: ({ row }) => {
      const rowIndex = Number(row.id);
      const { uuid, name } = customersList.value[rowIndex];

      return h(CustomerViewButton, {
        onClicked: () => redirectToCustomerDetails({ id: uuid, name }),
      });
    },
  },
];

const { defaultParams, handleParamsChange } = useMemoDefaultParams(
  cookieKey.value,
  {
    pageSize: 10,
    sortDir: "asc",
    sortKey: "name",
    filter: "",
  },
);

const { columnVisibility } = useMemoColumnVisibility(
  `${cookieKey.value}-columns`,
);

const { columnSizeState } = useMemoDefaultColumnSizing({
  key: `${cookieKey.value}_column-sizing`,
});

const DEFAULT_COLUMN_ORDER = [
  "name",
  "seats_purchased",
  "seats_used",
  "total_usage",
  "created_at",
];

const { columnOrder, resetColumnSizing } = useMemoDefaultColumnOrder({
  key: `${cookieKey.value}_column-order`,
  state: DEFAULT_COLUMN_ORDER,
});

if (
  columnOrder.value.length !== DEFAULT_COLUMN_ORDER.length ||
  !DEFAULT_COLUMN_ORDER.every((col) => columnOrder.value.includes(col))
) {
  columnOrder.value = DEFAULT_COLUMN_ORDER;
}

const {
  paramsState,
  filterState,
  sortingState,
  paginationState,
  handlePageSizeChange,
} = useServerSidePagination({
  pageIndex: 0,
  pageSize: defaultParams.value.pageSize,
  sortDir: defaultParams.value.sortDir,
  sortKey: defaultParams.value.sortKey,
  filter: "",
});

const emptyState = computed(() =>
  paramsState.filter
    ? {
        icon: "cl-icon-empty-folder",
        title: t("no-search-results-default"),
        description: t("subtext-no-search-results-default"),
      }
    : {
        icon: "cl-icon-empty-box",
        title: t("no-search-results-default"),
        description: t("no-customers"),
      },
);

const router = useRouter();

const redirectToCustomerDetails = (row: { id: string; name: string }) =>
  redirectToCustomerUsage(row, {
    router,
    setCustomer,
    clearAzureCache,
    clearLicenceCache,
  });

const table = useVueTable({
  initialState: {
    columnPinning: {
      right: ["actions"],
    },
  },
  get data() {
    return customersList.value;
  },
  get pageCount() {
    return Math.ceil(total.value.total / defaultParams.value.pageSize);
  },
  columns: columns,
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  getFilteredRowModel: getFilteredRowModel(),
  getSortedRowModel: getSortedRowModel(),
  onPaginationChange: (updater) => {
    paginationState.value =
      updater instanceof Function ? updater(paginationState.value) : updater;
  },
  onSortingChange: (updater) => {
    sortingState.value =
      updater instanceof Function ? updater(sortingState.value) : updater;
  },
  onColumnSizingChange: (updater) => {
    columnSizeState.value =
      updater instanceof Function ? updater(columnSizeState.value) : updater;
  },
  onColumnOrderChange: (updater) => {
    columnOrder.value =
      updater instanceof Function ? updater(columnOrder.value) : updater;
  },
  state: {
    get pagination() {
      return paginationState.value;
    },
    get sorting() {
      return sortingState.value;
    },
    get columnVisibility() {
      return columnVisibility.value;
    },
    get columnSizing() {
      return columnSizeState.value;
    },
    get columnOrder() {
      return columnOrder.value;
    },
  },
  manualPagination: true,
  enableSortingRemoval: false,
  columnResizeMode: "onChange",
  defaultColumn: {
    minSize: MIN_COLUMN_SIZE,
    size: calcColumnDynamicSize(
      RATIO_SIZE - 80,
      columns.length,
      columnVisibility.value,
    ),
    maxSize: MAX_COLUMN_SIZE,
  },
});

const { targetItemIndex, onDrop, onDragEnter, onDragStart, onDragEnd } =
  useColumnOrder(table);

const debounceSearch = useDebounceFn((phrase: string) => {
  filterState.value = phrase;
}, 500);

const rowsIndexPointer = useRowsIndexPointer(table);
const skeletonArray = useSkeletonArray(table, 10);
const { columnsToggleList, toggleColumnVisibility } = useColumnVisibility(
  table,
  columnVisibility,
);

watch(
  paramsState,
  () => {
    if (selectedServiceUuid.value) {
      getOrganisationsList(selectedServiceUuid.value);
    }

    handleParamsChange({
      pageSize: paramsState.results,
      sortDir: paramsState.sortDir,
      sortKey: paramsState.sortKey,
      filter: paramsState.filter,
    });
  },
  { immediate: true, deep: true },
);

watch(selectedServiceUuid, async (value: string) => {
  await getOrganisationsList(value);
});
</script>
